(() => {
  const elementName = 'happysoy-set-teaser';
  if (customElements.get(elementName)) return;

  const money = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
  const endpoints = { catalog: '/apps/happysoy-bundles/api/mix/catalog', savings: '/apps/happysoy-bundles/api/mix/savings' };
  const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/;
  const maxAgeMs = 60000, timeoutMs = 6000;

  function endpointUrl(value, expected) {
    const url = new URL(value || expected, location.origin);
    if (url.origin !== location.origin || url.pathname !== expected || url.search || url.hash || url.username || url.password) throw new Error('Invalid teaser URL');
    return url.href;
  }

  function validateCatalog(value) {
    if (!value || !Array.isArray(value.types)) throw new Error('Invalid type catalog');
    const types = new Map();
    for (const type of value.types) {
      if (!type || !uuid.test(type.id) || types.has(type.id) || !Number.isSafeInteger(type.revision) || type.revision < 1
        || !Number.isSafeInteger(type.price) || type.price <= 0) throw new Error('Invalid type catalog');
      types.set(type.id, type);
    }
    return types;
  }

  function savingsRows(value, types) {
    const now = Date.now();
    if (value?.scope !== 'configured-topics' || !Array.isArray(value.types) || value.pending !== false
      || !Number.isSafeInteger(value.checkedAt) || value.checkedAt <= 0 || value.checkedAt > now + 1000 || now - value.checkedAt >= maxAgeMs) return null;
    const rows = new Map(), seen = new Set();
    for (const row of value.types) {
      if (!row || !uuid.test(row.id) || seen.has(row.id)) return null;
      seen.add(row.id);
      const type = types.get(row.id), saving = row.savings;
      if (!type || row.revision !== type.revision || row.price !== type.price || !saving) continue;
      const { originalMin, savingMin, percentMin } = saving;
      if (![originalMin, savingMin, percentMin].every(Number.isSafeInteger) || originalMin <= type.price
        || savingMin !== originalMin - type.price || percentMin < 0 || percentMin >= 100
        || percentMin !== Number(BigInt(savingMin) * 100n / BigInt(originalMin))) continue;
      if (saving.basis === 'fixed-normal-price' && (saving.originalMax !== originalMin || saving.savingMax !== savingMin || saving.percent !== percentMin)) continue;
      rows.set(row.id, saving);
    }
    return rows;
  }

  function hideSavings(element) {
    for (const amount of element.querySelectorAll('[data-saving-amount], [data-saving-original], [data-saving-comparison]')) { amount.hidden = true; amount.textContent = ''; }
    for (const badge of element.querySelectorAll('[data-set-saving]')) {
      badge.hidden = true;
      const percent = badge.querySelector('[data-saving-percent]');
      if (percent) percent.textContent = '';
    }
  }

  customElements.define(elementName, class extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.onPageShow = event => { if (event.persisted) this.refresh(); };
      window.addEventListener('pageshow', this.onPageShow);
      this.onVisibilityChange = () => {
        if (document.visibilityState === 'hidden') { this.cancelWork(); hideSavings(this); }
        else this.refresh();
      };
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.refresh();
    }

    disconnectedCallback() {
      this.connected = false;
      window.removeEventListener('pageshow', this.onPageShow);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.cancelWork();
      hideSavings(this);
    }

    cancelWork() {
      this.generation = null;
      clearTimeout(this.expiryTimer);
      clearTimeout(this.refreshTimer);
      this.expiryTimer = this.refreshTimer = null;
      this.cancelRequest?.();
      this.cancelDelay?.();
      this.cancelRequest = this.cancelDelay = null;
    }

    current(generation) { return this.connected && this.generation === generation; }

    async json(url) {
      const controller = new AbortController();
      let timer;
      const cancel = () => { controller.abort(); rejectDeadline(new Error('Teaser request cancelled')); };
      let rejectDeadline;
      const deadline = new Promise((_, reject) => { rejectDeadline = reject; timer = setTimeout(cancel, timeoutMs); });
      this.cancelRequest = cancel;
      try {
        return await Promise.race([
          fetch(url, { cache: 'no-store', credentials: 'same-origin', redirect: 'error', signal: controller.signal }).then(response => {
            if (!response.ok) throw new Error('Teaser data unavailable');
            return response.json();
          }), deadline,
        ]);
      } finally {
        clearTimeout(timer);
        if (this.cancelRequest === cancel) this.cancelRequest = null;
      }
    }

    delay() {
      return new Promise(resolve => {
        const finish = () => { clearTimeout(timer); if (this.cancelDelay === finish) this.cancelDelay = null; resolve(); };
        const timer = setTimeout(finish, 1000);
        this.cancelDelay = finish;
      });
    }

    async refresh() {
      this.cancelWork();
      hideSavings(this);
      const cards = [...this.querySelectorAll('[data-set-card][data-type-id]')];
      for (const card of cards) {
        const price = card.querySelector('[data-set-price]');
        if (price) { price.hidden = true; price.textContent = ''; }
      }
      if (!this.connected || !cards.length || document.visibilityState === 'hidden') return;
      const generation = {};
      this.generation = generation;
      try {
        const catalogUrl = endpointUrl(this.dataset.catalogUrl, endpoints.catalog);
        const savingsUrl = endpointUrl(this.dataset.savingsUrl, endpoints.savings);
        const builderUrl = new URL(this.dataset.builderUrl || '/pages/geschenksets', location.origin);
        if (builderUrl.origin !== location.origin || builderUrl.username || builderUrl.password) throw new Error('Invalid builder URL');
        builderUrl.searchParams.delete('bundle');
        builderUrl.searchParams.delete('type');
        const types = validateCatalog(await this.json(catalogUrl));
        if (!this.current(generation)) return;
        for (const card of cards) {
          const type = types.get(card.dataset.typeId), target = new URL(builderUrl.href);
          if (type) target.searchParams.set('type', type.id);
          for (const link of card.querySelectorAll('[data-set-link]')) link.setAttribute('href', `${target.pathname}${target.search}${target.hash}`);
          const price = card.querySelector('[data-set-price]');
          if (price && type) { price.textContent = money.format(type.price / 100); price.hidden = false; }
        }
        // Same type-level comparison as the builder.
        // Optional prices never delay links, and a cold catalog gets only bounded retries.
        for (let attempt = 0; attempt < 4 && this.current(generation); attempt++) {
          const value = await this.json(savingsUrl);
          if (!this.current(generation)) return;
          const rows = savingsRows(value, types);
          if (rows) {
            for (const card of cards) {
              const saving = rows.get(card.dataset.typeId);
              if (!saving) continue;
              const fixed = saving.basis === 'fixed-normal-price';
              const amount = card.querySelector('[data-saving-amount]');
              if (amount) {
                const template = fixed ? this.dataset.savingFixedTemplate || 'Du sparst {amount}' : this.dataset.savingAmountTemplate || 'Mindestens {amount} sparen';
                amount.textContent = template.replace('{amount}', money.format(saving.savingMin / 100));
                amount.hidden = false;
              }
              const original = card.querySelector('[data-saving-original]');
              if (original) {
                const template = fixed ? this.dataset.originalFixedTemplate || 'Einzeln {amount}' : this.dataset.originalMinimumTemplate || 'Einzeln ab {amount}';
                original.textContent = template.replace('{amount}', money.format(saving.originalMin / 100));
                original.hidden = false;
              }
              const badge = card.querySelector('[data-set-saving]'), percent = badge?.querySelector('[data-saving-percent]');
              const qualifier = badge?.querySelector('[data-saving-qualifier]');
              if (qualifier) qualifier.hidden = fixed;
              if (percent && saving.percentMin > 0) { percent.textContent = `${saving.percentMin} %`; badge.hidden = false; }
            }
            const comparison = this.querySelector('[data-saving-comparison]');
            if (comparison && rows.size) {
              const fixed = [...rows.values()].every(saving => saving.basis === 'fixed-normal-price');
              comparison.textContent = fixed
                ? this.dataset.comparisonFixed || 'Ersparnis gegenüber dem Einzelkauf.'
                : this.dataset.comparisonMinimum || 'Mindest-Ersparnis für die angezeigten Themen; bei einem eigenen Anlass hängt sie von den gewählten Motiven ab.';
              comparison.hidden = false;
            }
            const remaining = Math.max(0, value.checkedAt + maxAgeMs - Date.now());
            this.expiryTimer = setTimeout(() => {
              this.expiryTimer = null;
              if (!this.current(generation)) return;
              hideSavings(this);
              if (document.visibilityState === 'hidden') return;
              // Expire precisely, but do not spin on an almost-expired upstream
              // snapshot. All recurring work stops while the tab is hidden.
              const refresh = () => {
                this.refreshTimer = null;
                if (this.current(generation) && document.visibilityState !== 'hidden') this.refresh();
              };
              if (remaining < 1000) this.refreshTimer = setTimeout(refresh, 1000 - remaining);
              else refresh();
            }, remaining);
            return;
          }
          if (value?.pending !== true || value.scope !== 'configured-topics' || attempt === 3) return;
          await this.delay();
        }
      } catch {
        // Original theme links remain usable if either optional request fails.
      }
    }
  });
})();
